import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import validate from 'validate.js';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { Page, FilesDropzone, Header, Label, SelectField } from 'components';
import { Typography, TextField, InputAdornment, Grid, Card, Stack, CardHeader, CardContent, Divider, Autocomplete, createFilterOptions,
	Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import _ from 'underscore';
import useRouter from 'utils/useRouter';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { IsSafeToDeleteConfigurationRequest } from 'views/IncidentTracker/requests/IncidentTrackerConfigurationRequests';
import { SetFormState, SetCurrentQueueDropdown, GetDropdowns, GetProjectsDropdown, GetSowDropdown, GetLayersDropdown, GetTypeDropdown,
	GetDetailedTypeDropdown, GetIncidentBase, GetIncident, GetAllDropdowns, GetIncidentComments, GetIncidentFiles } from '../../../actions/IncidentTrackerActions';

import IncidentCommentsTable from './IncidentCommentsTable';
import IncidentFilesTable from './IncidentFilesTable';
import FileDropAndPrintZone from './FileDropAndPrintZone';

const schema = {
	siteId: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	project: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	sow: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	priority: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	genericType: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	type: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	detailedType: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	technologies: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	layers: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	currentQueue: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	needsRevisit: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	handoverBlocking: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	subject: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const useStyles = makeStyles((theme) => ({
	root: {},
	accordion: {
		backgroundColor: '#f5f5f5'
	},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1),
		},
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%',
	},
	divider: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	noBorder: {
		border: 'none',
	}
}));

const IncidentTrackerDialogForm = (props) => {
	const { incidentId, submitter, ...rest } = props;
	const classes = useStyles();
	const router = useRouter();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const setCurrentQueueDropdown = formState => dispatch(SetCurrentQueueDropdown(formState));
	const formState = useSelector((state) => state.incidentTrackerReducer.formState);
	const user = useSelector(state => state.session.user);
	const submitterName = incidentId !== 0 ? submitter : `${user.firstname} ${user.lastname}`;
	const dropdowns = useSelector((state) => state.incidentTrackerReducer.dropdowns);
	const projectsDropdown = useSelector((state) => state.incidentTrackerReducer.projectsDropdown);
	const sowDropdown = useSelector((state) => state.incidentTrackerReducer.sowDropdown);
	const layersDropdown = useSelector((state) => state.incidentTrackerReducer.layersDropdown);
	const typeDropdown = useSelector((state) => state.incidentTrackerReducer.typeDropdown);
	const detailedTypeDropdown = useSelector((state) => state.incidentTrackerReducer.detailedTypeDropdown);
	const currentQueueDropdown = useSelector((state) => state.incidentTrackerReducer.currentQueueDropdown);
	const incidentComments = useSelector((state) => state.incidentTrackerReducer.incidentComments);
	const incidentFiles = useSelector((state) => state.incidentTrackerReducer.incidentFiles);
	const yesNoDropdown = [{ value: 'Yes', text: 'Yes' }, { value: 'No', text: 'No' }];
	const [expanded, setExpanded] = useState('');

	const _handleAccordionChange = (panel) => (event, isExpanded) => {
		setExpanded(isExpanded ? panel : false);
	};

	const _hasError = field => (incidentId !== 0 && formState.errors[field]) || !!(formState.touched[field] && formState.errors[field]);

	const filterOptions = createFilterOptions({
		limit: 20
	});

	const _handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleSiteIdChange = (option) => {
		if (option !== null) {
			dispatch(GetProjectsDropdown(option.value));
			setFormState({
				...formState,
				values: {
					siteId: option.value
				},
				isValid: false,
				touched: {
					siteId: true
				},
			});
		} else {
			dispatch(GetProjectsDropdown(null));
			setFormState({
				values: {},
				isValid: false,
				touched: {},
				errors: {},
			});
		}
	};

	const _handleTechnologiesChange = (event) => {
		if (event.target.value != null) {
			dispatch(GetLayersDropdown(event.target.value));
		}
		setFormState({
			...formState,
			values: {
				...formState.values,
				layers: [],
				technologies: event.target.value === null ? [] : event.target.value,
			},
			touched: {
				...formState.touched,
				layers: false,
				technologies: true,
			},
		});
	};

	const _handleProjectChange = (event) => {
		dispatch(GetSowDropdown(formState.values.siteId, event.target.value));
		_handleChange(event);
	};

	const _handleSowChange = (event) => {
		dispatch(GetIncidentBase(formState.values.siteId, formState.values.project, event.target.value));
		_handleChange(event);
	};

	const _handleGenericTypeChange = (event) => {
		if (event.target.value != null) {
			dispatch(GetTypeDropdown(event.target.value));
		}
		setFormState({
			...formState,
			values: {
				...formState.values,
				genericType: event.target.value === null ? '' : event.target.value,
				type: '',
				detailedType: ''
			},
			touched: {
				...formState.touched,
				genericType: true,
				type: false,
				detailedType: false
			},
		});
	};

	const _handleTypeChange = (event) => {
		dispatch(GetDetailedTypeDropdown(event.target.value));
		setFormState({
			...formState,
			values: {
				...formState.values,
				type: event.target.value === null ? '' : event.target.value,
				detailedType: ''
			},
			touched: {
				...formState.touched,
				type: true,
				detailedType: false
			},
		});
	};

	useEffect(() => {
		if (incidentId === 0) {
			dispatch(GetDropdowns());
		} else {
			dispatch(GetIncident(incidentId));
			dispatch(GetAllDropdowns(incidentId));
			dispatch(GetIncidentComments(incidentId));
			dispatch(GetIncidentFiles(incidentId));
		}
	}, []);

	useMemo(() => {
		const newTouched = {};

		if (formState.submit) {
			_.each(_.keys(schema), item => {
				newTouched[item] = true;
			});
		}
		const errors = validate(formState.values, schema, {
			fullMessages: false
		});

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
			touched: {
				...formState.touched,
				...newTouched
			}
		});
	}, [formState.values, formState.submit]);

	useMemo(() => {
		setCurrentQueueDropdown([
			{ value: 'BT', text: 'BT', disabled: true },
			{ value: 'Celfinet', text: 'Celfinet' },
			{ value: 'FOPS', text: 'FOPS' },
			{ value: 'EGI', text: 'EGI' },
			{ value: 'Delivery', text: 'Delivery' },
			{ value: 'BT Operations', text: 'BT Operations' },
			{ value: 'BT Acceptance', text: 'BT Acceptance' },
			{ value: formState.values.partner, text: formState.values.partner }
		]);
	}, [formState.values.partner]);

	return (
		<div className={classes.fields}>
			<Grid container spacing={2}>
				<Grid item md={9} xs={9}>
					<Grid container spacing={2}>
						{/* New row */}
						<Grid item md={12} xs={12}>
							<Divider><Typography variant="h5" gutterBottom component="div">Work</Typography></Divider>
						</Grid>
						<Grid item md={4} xs={4}>
							<Autocomplete
								id="siteId"
								name="siteId"
								hintText="Site Id"
								error={_hasError('siteId')}
								helperText={
									_hasError('siteId')
										? formState.errors.siteId[0]
										: null
								}
								disabled={incidentId !== 0}
								options={dropdowns.sites || []}
								value={{ value: formState.values.siteId || '', text: formState.values.siteId || '' }}
								getOptionLabel={(option) => `${option.text}`}
								renderInput={(params) => <TextField {...params} label="Site ID" variant="outlined" />}
								filterOptions={filterOptions}
								onChange={(event, value) => { _handleSiteIdChange(value); }}
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<SelectField
								id="project"
								name="project"
								hintText="Project"
								error={_hasError('project')}
								helperText={
									_hasError('project')
										? formState.errors.project[0]
										: null
								}
								disabled={incidentId !== 0}
								defaultValue=""
								menuItems={projectsDropdown || []}
								value={formState.values.project || ''}
								onChange={_handleProjectChange}
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<SelectField
								id="sow"
								name="sow"
								hintText="SOW"
								error={_hasError('sow')}
								helperText={
									_hasError('sow')
										? formState.errors.sow[0]
										: null
								}
								disabled={incidentId !== 0}
								defaultValue=""
								menuItems={sowDropdown || []}
								value={formState.values.sow || ''}
								onChange={_handleSowChange}
							/>
						</Grid>
						<Grid item md={12} xs={12}>
							<Divider><Typography variant="h5" gutterBottom component="div">Incident Details</Typography></Divider>
						</Grid>
						{/* New row */}
						<Grid item md={4} xs={4}>
							<TextField
								id="submitter"
								name="submitter"
								label="Submitter"
								hintText="Submitter"
								error={_hasError('submitter')}
								helperText={
									_hasError('submitter')
										? formState.errors.submitter[0]
										: null
								}
								variant="outlined"
								disabled
								fullWidth
								value={submitterName || ''}
								InputLabelProps={{
									shrink: true,
								}}
								InputProps={{ startAdornment: (<InputAdornment position="start"><AccountCircle /></InputAdornment>) }}
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<SelectField
								id="status"
								name="status"
								error={_hasError('status')}
								helperText={
									_hasError('status')
										? formState.errors.status[0]
										: null
								}
								hintText="Status"
								disabled={incidentId === 0}
								defaultValue=""
								menuItems={dropdowns.status || []}
								value={formState.values.status || ''}
								onChange={_handleChange}
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<SelectField
								id="priority"
								name="priority"
								hintText="Priority"
								error={_hasError('priority')}
								helperText={
									_hasError('priority')
										? formState.errors.priority[0]
										: null
								}
								defaultValue=""
								menuItems={dropdowns.priority || []}
								value={formState.values.priority || ''}
								onChange={_handleChange}
							/>
						</Grid>
						{/* New row */}
						<Grid item md={4} xs={4}>
							<SelectField
								id="genericType"
								name="genericType"
								hintText="Generic Type"
								error={_hasError('genericType')}
								helperText={
									_hasError('genericType')
										? formState.errors.genericType[0]
										: null
								}
								defaultValue=""
								menuItems={dropdowns.genericType || []}
								value={formState.values.genericType || ''}
								onChange={_handleGenericTypeChange}
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<SelectField
								id="type"
								name="type"
								hintText="Type"
								error={_hasError('type')}
								helperText={
									_hasError('type')
										? formState.errors.type[0]
										: null
								}
								defaultValue=""
								menuItems={typeDropdown || []}
								value={formState.values.type || ''}
								onChange={_handleTypeChange}
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<SelectField
								id="detailedType"
								name="detailedType"
								hintText="Detailed Type"
								error={_hasError('detailedType')}
								helperText={
									_hasError('detailedType')
										? formState.errors.detailedType[0]
										: null
								}
								defaultValue=""
								menuItems={detailedTypeDropdown || []}
								value={formState.values.detailedType || ''}
								onChange={_handleChange}
							/>
						</Grid>
						{/* New row */}
						<Grid item md={4} xs={4}>
							<SelectField
								id="technologies"
								name="technologies"
								hintText="Technologies"
								error={_hasError('technologies')}
								helperText={
									_hasError('technologies')
										? formState.errors.technologies[0]
										: null
								}
								isMultiSelect
								menuItems={dropdowns.technologies || []}
								value={formState.values.technologies || []}
								onChange={_handleTechnologiesChange}
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<SelectField
								id="layers"
								name="layers"
								hintText="Layers"
								error={_hasError('layers')}
								helperText={
									_hasError('layers')
										? formState.errors.layers[0]
										: null
								}
								isMultiSelect
								menuItems={layersDropdown || []}
								value={formState.values.layers || []}
								onChange={_handleChange}
							/>
						</Grid>
						<Grid item md={2} xs={2}>
							<SelectField
								id="currentQueue"
								name="currentQueue"
								hintText="Current Queue"
								error={_hasError('currentQueue')}
								helperText={
									_hasError('currentQueue')
										? formState.errors.currentQueue[0]
										: null
								}
								defaultValue=""
								menuItems={currentQueueDropdown || []}
								value={formState.values.currentQueue || ''}
								onChange={_handleChange}
							/>
						</Grid>
						{/* New row */}
						<Grid item md={2} xs={2}>
							<SelectField
								id="needsRevisit"
								name="needsRevisit"
								hintText="Needs Revisit"
								error={_hasError('needsRevisit')}
								helperText={
									_hasError('needsRevisit')
										? formState.errors.needsRevisit[0]
										: null
								}
								defaultValue=""
								menuItems={yesNoDropdown}
								value={formState.values.needsRevisit || ''}
								onChange={_handleChange}
							/>
						</Grid>
						<Grid item md={4} xs={4}>
							<TextField
								id="subject"
								name="subject"
								label="Subject"
								error={_hasError('subject')}
								helperText={
									_hasError('subject')
										? formState.errors.subject[0]
										: null
								}
								variant="outlined"
								disabled={incidentId !== 0}
								fullWidth
								value={formState.values.subject || ''}
								InputLabelProps={{
									shrink: true,
								}}
								onChange={_handleChange}
							/>
						</Grid>
						<Grid item md={3} xs={3}>
							<TextField
								id="incidentNumber"
								name="incidentNumber"
								label="Incident Number"
								variant="outlined"
								fullWidth
								value={formState.values.incidentNumber || ''}
								InputLabelProps={{
									shrink: true,
								}}
								onChange={_handleChange}
							/>
						</Grid>
						<Grid item md={3} xs={3}>
							<TextField
								id="taskNumber"
								name="taskNumber"
								label="Task Number"
								variant="outlined"
								fullWidth
								value={formState.values.taskNumber || ''}
								InputLabelProps={{
									shrink: true,
								}}
								onChange={_handleChange}
							/>
						</Grid>
						<Grid item md={2} xs={2}>
							<SelectField
								id="handoverBlocking"
								name="handoverBlocking"
								hintText="Handover Blocking"
								error={_hasError('handoverBlocking')}
								helperText={
									_hasError('handoverBlocking')
										? formState.errors.handoverBlocking[0]
										: null
								}
								defaultValue=""
								menuItems={yesNoDropdown}
								value={formState.values.handoverBlocking || ''}
								onChange={_handleChange}
							/>
						</Grid>
						{/* New row */}
						<Grid item md={12} xs={12}>
							<Accordion className={classes.accordion} expanded={expanded === 'panel1'} onChange={_handleAccordionChange('panel1')}>
								<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
									<Typography>Comments</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Grid item lg={12} xs={12}>
										<IncidentCommentsTable data={incidentComments} />
									</Grid>
									<TextField
										id="observations"
										name="observations"
										error={_hasError('observations')}
										helperText={
											_hasError('observations')
												? formState.errors.observations[0]
												: null
										}
										variant="outlined"
										multiline
										rows={2}
										value={formState.values.observations || ''}
										fullWidth
										InputLabelProps={{
											shrink: true,
										}}
										onChange={_handleChange}
									/>
								</AccordionDetails>
							</Accordion>
						</Grid>
						<Grid item md={12} xs={12}>
							<Accordion className={classes.accordion} expanded={expanded === 'panel2'} onChange={_handleAccordionChange('panel2')}>
								<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2a-content" id="panel2a-header">
									<Typography>File History</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Grid item lg={12} xs={12}>
										<IncidentFilesTable data={incidentFiles} />
									</Grid>
								</AccordionDetails>
							</Accordion>
						</Grid>
					</Grid>
				</Grid>
				<Grid item md={3} xs={3}>
					<Card>
						<CardHeader
							title="Work details"
							titleTypographyProps={{ align: 'center' }}
						/>
						<CardContent>
							<Stack spacing={2}>
								{/* New row */}
								<TextField
									id="key"
									name="key"
									label="Key"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.key || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
								{/* New row */}
								<TextField
									id="siteTrackerPRef"
									name="siteTrackerPRef"
									label="Site Tracker P-Ref"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.siteTrackerPRef || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
								{/* New row */}
								<TextField
									id="bo"
									name="bo"
									label="BO"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.bo || ''}
									InputLabelProps={{
										shrink: true,
										classes: { notchedOutline: classes.noBorder }
									}}
								/>
								{/* New row */}
								<TextField
									id="partner"
									name="partner"
									label="Partner"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.partner || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
								{/* New row */}
								<TextField
									id="vendor"
									name="vendor"
									label="Vendor"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.vendor || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
								{/* New row */}
								<TextField
									id="operator"
									name="operator"
									label="Operator"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.operator || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
								{/* New row */}
								<TextField
									id="client"
									name="client"
									label="Client"
									variant="filled"
									disabled
									fullWidth
									value={formState.values.client || ''}
									InputLabelProps={{
										shrink: true,
									}}
								/>
							</Stack>
						</CardContent>
					</Card>
				</Grid>
				<Grid item md={12} xs={12}>
					<Accordion className={classes.accordion} expanded={expanded === 'panel3'} onChange={_handleAccordionChange('panel3')}>
						<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel3a-content" id="panel3a-header">
							<Typography>Attachments</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<FileDropAndPrintZone multiple acceptedTypes=".xlsx,.txt,.png,.jpg" />
						</AccordionDetails>
					</Accordion>
				</Grid>
			</Grid>
		</div>
	);
};

IncidentTrackerDialogForm.propTypes = {
	incidentId: PropTypes.number,
	submitter: PropTypes.string
};

export default IncidentTrackerDialogForm;
